import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import Header from '../components/layout/Header';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiCalendar,
  FiClock,
  FiPhone,
  FiMapPin,
  FiFileText,
  FiVideo,
  FiShield,
  FiAlertTriangle,
  FiInfo
} from 'react-icons/fi';
import { FaHeartbeat, FaSyringe, FaFileMedical, FaNotesMedical } from 'react-icons/fa';

interface HealthRecord {
  immunizations: Immunization[];
  appointments: Appointment[];
  insurance: InsuranceInfo;
  medicalHistory: MedicalHistory;
  requirements: HealthRequirement[];
}

interface Immunization {
  id: string;
  name: string;
  date: string;
  provider: string;
  status: 'complete' | 'incomplete' | 'expired';
  expirationDate?: string;
}

interface Appointment {
  id: string;
  date: string;
  time: string;
  type: 'in-person' | 'telehealth';
  provider: string;
  reason: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  location?: string;
}

interface InsuranceInfo {
  provider: string;
  policyNumber: string;
  groupNumber: string;
  effectiveDate: string;
  coverageType: string;
}

interface MedicalHistory {
  bloodType?: string;
  allergies: string[];
  medications: string[];
  conditions: string[];
}

interface HealthRequirement {
  id: string;
  name: string;
  description: string;
  dueDate: string;
  status: 'complete' | 'pending' | 'overdue';
}

const HealthPortalPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [healthRecord, setHealthRecord] = useState<HealthRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'appointments' | 'immunizations' | 'insurance' | 'history'>('overview');

  useEffect(() => {
    const fetchHealthData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch health record
        const recordRef = doc(db, 'healthRecords', currentUser.uid);
        const recordSnap = await getDoc(recordRef);

        if (recordSnap.exists()) {
          setHealthRecord(recordSnap.data() as HealthRecord);
        } else {
          setError('Health record not found. Please contact Student Health Services.');
        }
      } catch (err) {
        console.error('Error fetching health data:', err);
        setError('Failed to load health information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchHealthData();
  }, [currentUser]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      complete: { icon: <FiCheckCircle size={14} />, text: 'Complete', color: 'bg-green-100 text-green-800' },
      scheduled: { icon: <FiClock size={14} />, text: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
      completed: { icon: <FiCheckCircle size={14} />, text: 'Completed', color: 'bg-green-100 text-green-800' },
      cancelled: { icon: <FiAlertCircle size={14} />, text: 'Cancelled', color: 'bg-gray-100 text-gray-800' },
      pending: { icon: <FiClock size={14} />, text: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      incomplete: { icon: <FiAlertCircle size={14} />, text: 'Incomplete', color: 'bg-red-100 text-red-800' },
      expired: { icon: <FiAlertTriangle size={14} />, text: 'Expired', color: 'bg-red-100 text-red-800' },
      overdue: { icon: <FiAlertTriangle size={14} />, text: 'Overdue', color: 'bg-red-100 text-red-800' }
    };

    const badge = badges[status as keyof typeof badges];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-uc-navy">Loading health information...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!healthRecord) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600">Failed to load health information</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">HEALTH PORTAL</h1>
          <p className="text-gray-600 mt-2">Manage your health records and appointments</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5" size={20} />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Contact Information Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex flex-wrap items-start gap-6">
            <div className="flex items-start gap-3 flex-1 min-w-[200px]">
              <FiPhone className="text-blue-600 mt-1" size={20} />
              <div>
                <p className="font-bold text-blue-900">24/7 Nurse Advice Line</p>
                <p className="text-blue-800">(209) 228-2273</p>
              </div>
            </div>
            <div className="flex items-start gap-3 flex-1 min-w-[200px]">
              <FiMapPin className="text-blue-600 mt-1" size={20} />
              <div>
                <p className="font-bold text-blue-900">Health Center</p>
                <p className="text-blue-800">Health Services Building, Room 100</p>
              </div>
            </div>
            <div className="flex items-start gap-3 flex-1 min-w-[200px]">
              <FiClock className="text-blue-600 mt-1" size={20} />
              <div>
                <p className="font-bold text-blue-900">Hours</p>
                <p className="text-blue-800">Mon-Fri: 8:00 AM - 5:00 PM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-uc-gold text-uc-gold'
                    : 'border-transparent text-gray-600 hover:text-uc-navy'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'appointments'
                    ? 'border-uc-gold text-uc-gold'
                    : 'border-transparent text-gray-600 hover:text-uc-navy'
                }`}
              >
                Appointments
              </button>
              <button
                onClick={() => setActiveTab('immunizations')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'immunizations'
                    ? 'border-uc-gold text-uc-gold'
                    : 'border-transparent text-gray-600 hover:text-uc-navy'
                }`}
              >
                Immunizations
              </button>
              <button
                onClick={() => setActiveTab('insurance')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'insurance'
                    ? 'border-uc-gold text-uc-gold'
                    : 'border-transparent text-gray-600 hover:text-uc-navy'
                }`}
              >
                Insurance
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-uc-gold text-uc-gold'
                    : 'border-transparent text-gray-600 hover:text-uc-navy'
                }`}
              >
                Medical History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button className="flex items-center gap-4 p-6 bg-gradient-to-r from-uc-navy to-uc-navy-light text-white rounded-lg hover:opacity-90 transition-opacity">
                    <FiCalendar size={32} />
                    <div className="text-left">
                      <p className="font-bold text-lg">Schedule Appointment</p>
                      <p className="text-sm opacity-90">Book in-person or virtual visit</p>
                    </div>
                  </button>

                  <button className="flex items-center gap-4 p-6 bg-gradient-to-r from-uc-gold to-yellow-500 text-white rounded-lg hover:opacity-90 transition-opacity">
                    <FiVideo size={32} />
                    <div className="text-left">
                      <p className="font-bold text-lg">Telehealth Visit</p>
                      <p className="text-sm opacity-90">Connect with provider online</p>
                    </div>
                  </button>

                  <button className="flex items-center gap-4 p-6 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:opacity-90 transition-opacity">
                    <FiFileText size={32} />
                    <div className="text-left">
                      <p className="font-bold text-lg">Request Records</p>
                      <p className="text-sm opacity-90">Download medical documents</p>
                    </div>
                  </button>
                </div>

                {/* Health Requirements */}
                {healthRecord.requirements.length > 0 && (
                  <div>
                    <h3 className="text-xl font-bold text-uc-navy mb-4 flex items-center gap-2">
                      <FiShield size={24} className="text-uc-gold" />
                      Health Requirements
                    </h3>
                    <div className="space-y-3">
                      {healthRecord.requirements.map((req) => (
                        <div key={req.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-uc-navy">{req.name}</p>
                            <p className="text-sm text-gray-600">{req.description}</p>
                            <p className="text-sm text-gray-500 mt-1">Due: {formatDate(req.dueDate)}</p>
                          </div>
                          {getStatusBadge(req.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Appointments */}
                <div>
                  <h3 className="text-xl font-bold text-uc-navy mb-4 flex items-center gap-2">
                    <FiCalendar size={24} className="text-uc-gold" />
                    Upcoming Appointments
                  </h3>
                  {healthRecord.appointments.filter(a => a.status === 'scheduled').length > 0 ? (
                    <div className="space-y-3">
                      {healthRecord.appointments
                        .filter(a => a.status === 'scheduled')
                        .slice(0, 3)
                        .map((apt) => (
                          <div key={apt.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                            {apt.type === 'telehealth' ? (
                              <FiVideo className="text-uc-gold" size={24} />
                            ) : (
                              <FaHeartbeat className="text-uc-gold" size={24} />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-uc-navy">{apt.reason}</p>
                              <p className="text-sm text-gray-600">with {apt.provider}</p>
                              <p className="text-sm text-gray-500">{formatDate(apt.date)} at {apt.time}</p>
                            </div>
                            {getStatusBadge(apt.status)}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">No upcoming appointments scheduled</p>
                  )}
                </div>
              </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-uc-navy">All Appointments</h3>
                  <button className="bg-uc-gold text-white px-6 py-2 rounded-lg hover:bg-yellow-500 transition-colors">
                    Schedule New Appointment
                  </button>
                </div>

                <div className="space-y-4">
                  {healthRecord.appointments.map((apt) => (
                    <div key={apt.id} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {apt.type === 'telehealth' ? (
                            <div className="p-3 bg-blue-100 rounded-lg">
                              <FiVideo className="text-blue-600" size={24} />
                            </div>
                          ) : (
                            <div className="p-3 bg-green-100 rounded-lg">
                              <FaHeartbeat className="text-green-600" size={24} />
                            </div>
                          )}
                          <div>
                            <p className="font-bold text-uc-navy text-lg">{apt.reason}</p>
                            <p className="text-gray-600">{apt.type === 'telehealth' ? 'Virtual Visit' : 'In-Person Visit'}</p>
                          </div>
                        </div>
                        {getStatusBadge(apt.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Provider</p>
                          <p className="text-gray-900 font-medium">{apt.provider}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Date & Time</p>
                          <p className="text-gray-900 font-medium">{formatDate(apt.date)} at {apt.time}</p>
                        </div>
                        {apt.location && (
                          <div>
                            <p className="text-gray-600">Location</p>
                            <p className="text-gray-900 font-medium">{apt.location}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Immunizations Tab */}
            {activeTab === 'immunizations' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-uc-navy">Immunization Records</h3>
                  <button className="bg-uc-gold text-white px-6 py-2 rounded-lg hover:bg-yellow-500 transition-colors">
                    Upload Record
                  </button>
                </div>

                <div className="space-y-4">
                  {healthRecord.immunizations.map((imm) => (
                    <div key={imm.id} className="p-6 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <FaSyringe className="text-purple-600" size={24} />
                          </div>
                          <div>
                            <p className="font-bold text-uc-navy text-lg">{imm.name}</p>
                            <p className="text-gray-600">Administered: {formatDate(imm.date)}</p>
                          </div>
                        </div>
                        {getStatusBadge(imm.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Provider</p>
                          <p className="text-gray-900 font-medium">{imm.provider}</p>
                        </div>
                        {imm.expirationDate && (
                          <div>
                            <p className="text-gray-600">Expires</p>
                            <p className="text-gray-900 font-medium">{formatDate(imm.expirationDate)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insurance Tab */}
            {activeTab === 'insurance' && (
              <div>
                <h3 className="text-2xl font-bold text-uc-navy mb-6">Insurance Information</h3>

                <div className="bg-gradient-to-r from-uc-navy to-uc-navy-light text-white p-8 rounded-lg mb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <FiShield size={48} />
                    <div>
                      <p className="text-2xl font-bold">{healthRecord.insurance.provider}</p>
                      <p className="opacity-90">{healthRecord.insurance.coverageType}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="opacity-75 text-sm mb-1">Policy Number</p>
                      <p className="font-mono text-lg">{healthRecord.insurance.policyNumber}</p>
                    </div>
                    <div>
                      <p className="opacity-75 text-sm mb-1">Group Number</p>
                      <p className="font-mono text-lg">{healthRecord.insurance.groupNumber}</p>
                    </div>
                    <div>
                      <p className="opacity-75 text-sm mb-1">Effective Date</p>
                      <p className="text-lg">{formatDate(healthRecord.insurance.effectiveDate)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <FiInfo size={20} />
                    Insurance Information
                  </h4>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Keep your insurance card with you at all times</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Verify coverage before scheduling appointments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>Contact your insurance provider for questions about benefits</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Medical History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-uc-navy mb-4">Medical History</h3>
                  <button className="bg-uc-gold text-white px-6 py-2 rounded-lg hover:bg-yellow-500 transition-colors mb-6">
                    Update Medical History
                  </button>
                </div>

                {healthRecord.medicalHistory.bloodType && (
                  <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 mb-1">Blood Type</p>
                    <p className="text-3xl font-bold text-red-900">{healthRecord.medicalHistory.bloodType}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Allergies */}
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <FiAlertTriangle className="text-red-600" size={24} />
                      <h4 className="font-bold text-uc-navy text-lg">Allergies</h4>
                    </div>
                    {healthRecord.medicalHistory.allergies.length > 0 ? (
                      <ul className="space-y-2">
                        {healthRecord.medicalHistory.allergies.map((allergy, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                            <span className="text-gray-900">{allergy}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600">No known allergies</p>
                    )}
                  </div>

                  {/* Current Medications */}
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <FaFileMedical className="text-blue-600" size={24} />
                      <h4 className="font-bold text-uc-navy text-lg">Current Medications</h4>
                    </div>
                    {healthRecord.medicalHistory.medications.length > 0 ? (
                      <ul className="space-y-2">
                        {healthRecord.medicalHistory.medications.map((med, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                            <span className="text-gray-900">{med}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600">No current medications</p>
                    )}
                  </div>

                  {/* Medical Conditions */}
                  <div className="p-6 border border-gray-200 rounded-lg md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <FaNotesMedical className="text-purple-600" size={24} />
                      <h4 className="font-bold text-uc-navy text-lg">Medical Conditions</h4>
                    </div>
                    {healthRecord.medicalHistory.conditions.length > 0 ? (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {healthRecord.medicalHistory.conditions.map((condition, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                            <span className="text-gray-900">{condition}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600">No known medical conditions</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Emergency Information */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
            <FiAlertCircle size={24} />
            Emergency Information
          </h3>
          <p className="text-red-800 mb-4">
            <strong>In case of a medical emergency, call 911 or go to the nearest emergency room.</strong>
          </p>
          <div className="space-y-2 text-sm text-red-800">
            <p>
              <strong>Campus Police (24/7):</strong> (209) 228-2677
            </p>
            <p>
              <strong>Student Health Center After Hours:</strong> Call the 24/7 Nurse Advice Line at (209) 228-2273
            </p>
            <p>
              <strong>Mental Health Crisis Line:</strong> (209) 228-4266
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HealthPortalPage;
